import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Slider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Close,
  CompareArrows,
  Fullscreen,
  FullscreenExit,
  SwapHoriz,
  ViewColumn,
  ViewStream
} from '@mui/icons-material';
import SecureImage from '../Common/SecureImage';
import SecureVideo from '../Common/SecureVideo';

// Advanced admin compare: split / slider overlay, swap, zoom, fullscreen (shared by AI model + customer flows)
const ZOOM_MIN = 75;
const ZOOM_MAX = 200;
const ZOOM_STEP = 25;

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {number} [props.generationId]
 * @param {string|null} [props.titleSecondary] - Subtitle under the dialog title (e.g. model name)
 * @param {string} props.referenceLabel - Left column label in split view (e.g. "AI model reference")
 * @param {string|null} props.referenceSrc - Secure URL for the reference still image
 * @param {string|null} props.generatedSrc - Secure URL for generated preview (image or video stream)
 * @param {boolean} props.isVideoOutput - When true, slider overlay mode is hidden (video + clip path is poor UX)
 */
const GenerationCompareDialog = ({
  open,
  onClose,
  generationId,
  titleSecondary,
  referenceLabel,
  referenceSrc,
  generatedSrc,
  isVideoOutput
}) => {
  const [swapped, setSwapped] = useState(false);
  const [viewMode, setViewMode] = useState('split');
  const [sliderPosition, setSliderPosition] = useState(50);
  const [zoomPercent, setZoomPercent] = useState(100);
  const [fullScreen, setFullScreen] = useState(false);

  // Reset controls when opening; drop fullscreen when the dialog closes (covers ESC / backdrop click)
  useEffect(() => {
    if (open) {
      setSwapped(false);
      setViewMode('split');
      setSliderPosition(50);
      setZoomPercent(100);
    } else {
      setFullScreen(false);
    }
  }, [open]);

  const handleViewMode = useCallback((_e, next) => {
    if (next !== null) setViewMode(next);
  }, []);

  const canCompare = Boolean(referenceSrc && generatedSrc);
  const sliderModeEnabled = !isVideoOutput && canCompare;

  const leftSplitSrc = swapped ? generatedSrc : referenceSrc;
  const rightSplitSrc = swapped ? referenceSrc : generatedSrc;
  const leftSplitCaption = swapped ? 'Generated output' : referenceLabel;
  const rightSplitCaption = swapped ? referenceLabel : 'Generated output';

  // Slider stack: bottom = full-bleed layer, top = clipped "before" strip from the left
  const sliderBottomSrc = swapped ? referenceSrc : generatedSrc;
  const sliderTopSrc = swapped ? generatedSrc : referenceSrc;

  const scale = zoomPercent / 100;

  const renderZoomedImage = (src, alt) => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        minHeight: 280,
        overflow: 'auto',
        maxHeight: fullScreen ? 'calc(100vh - 220px)' : '58vh',
        bgcolor: 'background.paper',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        p: 1
      }}
    >
      <Box
        sx={{
          transform: `scale(${scale})`,
          transformOrigin: 'top center',
          transition: 'transform 0.15s ease-out'
        }}
      >
        <SecureImage
          src={src}
          alt={alt}
          style={{ maxWidth: 520, maxHeight: fullScreen ? '75vh' : '52vh', objectFit: 'contain', display: 'block' }}
        />
      </Box>
    </Box>
  );

  const renderGeneratedVideoPane = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 280,
        maxHeight: fullScreen ? 'calc(100vh - 220px)' : '58vh',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Box sx={{ transform: `scale(${scale})`, transformOrigin: 'center center' }}>
        <SecureVideo
          src={generatedSrc}
          style={{ maxWidth: '100%', maxHeight: fullScreen ? '70vh' : '52vh' }}
          controls
        />
      </Box>
    </Box>
  );

  // One split column: still uses zoom; generated video uses scaled player
  const renderSplitPane = (src, caption) => {
    if (src === generatedSrc && isVideoOutput) return renderGeneratedVideoPane();
    return renderZoomedImage(src, caption);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={fullScreen ? false : 'xl'}
      fullWidth={!fullScreen}
      fullScreen={fullScreen}
      PaperProps={{
        sx: fullScreen
          ? { borderRadius: 0, bgcolor: 'background.default' }
          : { borderRadius: 2, maxHeight: '92vh' }
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          pr: 1
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5} flex={1} minWidth={0}>
          <CompareArrows color="primary" />
          <Box minWidth={0}>
            <Typography variant="h6" component="span" display="block">
              Compare{generationId != null ? ` · Generation #${generationId}` : ''}
            </Typography>
            {titleSecondary ? (
              <Typography variant="body2" color="text.secondary" display="block" noWrap title={titleSecondary}>
                {titleSecondary}
              </Typography>
            ) : null}
          </Box>
        </Box>
        <Stack direction="row" alignItems="center" spacing={0.5} flexShrink={0}>
          <Tooltip title={swapped ? 'Original order' : 'Swap panes'}>
            <IconButton
              onClick={() => setSwapped((s) => !s)}
              disabled={!canCompare}
              aria-label="Swap reference and generated sides"
            >
              <SwapHoriz />
            </IconButton>
          </Tooltip>
          <Tooltip title={fullScreen ? 'Exit fullscreen' : 'Fullscreen'}>
            <IconButton
              onClick={() => setFullScreen((f) => !f)}
              disabled={!canCompare}
              aria-label={fullScreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              {fullScreen ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton onClick={onClose} aria-label="Close compare dialog" edge="end">
              <Close />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 1 }}>
        {!canCompare ? (
          <Typography color="text.secondary">Nothing to compare for this generation.</Typography>
        ) : (
          <>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
              sx={{ mb: 2, flexWrap: 'wrap' }}
            >
              <ToggleButtonGroup
                exclusive
                size="small"
                value={viewMode}
                onChange={handleViewMode}
                aria-label="Compare layout"
              >
                <ToggleButton value="split" aria-label="Side by side">
                  <Stack direction="row" alignItems="center" spacing={0.5} component="span">
                    <ViewColumn sx={{ fontSize: 18 }} />
                    <span>Split</span>
                  </Stack>
                </ToggleButton>
                <ToggleButton value="slider" disabled={!sliderModeEnabled} aria-label="Slider overlay">
                  <Stack direction="row" alignItems="center" spacing={0.5} component="span">
                    <ViewStream sx={{ fontSize: 18 }} />
                    <span>Slider</span>
                  </Stack>
                </ToggleButton>
              </ToggleButtonGroup>
              {isVideoOutput ? (
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                  Slider overlay is available for image output only.
                </Typography>
              ) : null}
              <Box sx={{ minWidth: { xs: '100%', sm: 220 }, flex: { sm: '1 1 240px' }, maxWidth: 400 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Zoom {zoomPercent}%
                </Typography>
                <Slider
                  size="small"
                  value={zoomPercent}
                  min={ZOOM_MIN}
                  max={ZOOM_MAX}
                  step={ZOOM_STEP}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${v}%`}
                  onChange={(_e, v) => setZoomPercent(v)}
                  disabled={viewMode === 'slider' && !isVideoOutput}
                />
                {viewMode === 'slider' && !isVideoOutput ? (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Zoom applies in split view; slider overlay uses fit-to-frame.
                  </Typography>
                ) : null}
              </Box>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {viewMode === 'split' ? (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                      {leftSplitCaption}
                    </Typography>
                    {renderSplitPane(leftSplitSrc, leftSplitCaption)}
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                      {rightSplitCaption}
                    </Typography>
                    {renderSplitPane(rightSplitSrc, rightSplitCaption)}
                  </Paper>
                </Grid>
              </Grid>
            ) : (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                  Drag the slider — left: {swapped ? 'generated' : 'reference'} · full frame:{' '}
                  {swapped ? 'reference' : 'generated'}
                </Typography>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: { xs: 320, md: fullScreen ? 'min(78vh, 900px)' : 480 },
                    maxHeight: fullScreen ? '78vh' : 560,
                    bgcolor: 'grey.900',
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  {/* Base layer fills the stage; object-fit keeps aspect (same box as top for pixel alignment) */}
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none'
                    }}
                  >
                    <SecureImage
                      src={sliderBottomSrc}
                      alt="Compare base layer"
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  </Box>
                  {/* Top layer uses the same frame, clipped from the right so the left strip reveals the overlay image */}
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`,
                      pointerEvents: 'none'
                    }}
                  >
                    <SecureImage
                      src={sliderTopSrc}
                      alt="Compare overlay layer"
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: `calc(${sliderPosition}% - 2px)`,
                      width: 4,
                      bgcolor: 'primary.main',
                      boxShadow: '0 0 12px rgba(0,0,0,0.45)',
                      pointerEvents: 'none',
                      zIndex: 2
                    }}
                  />
                </Box>
                <Slider
                  value={sliderPosition}
                  min={0}
                  max={100}
                  onChange={(_e, v) => setSliderPosition(v)}
                  sx={{ mt: 2, maxWidth: 640, mx: 'auto', display: 'block' }}
                  aria-label="Compare reveal position"
                />
                <Stack direction="row" justifyContent="space-between" sx={{ maxWidth: 640, mx: 'auto', mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {swapped ? 'Generated (peel)' : 'Reference (peel)'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {swapped ? 'Reference (base)' : 'Generated (base)'}
                  </Typography>
                </Stack>
              </Paper>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GenerationCompareDialog;
